import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatDateLong } from '../../utils/dateUtils';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  TextField,
  Grid,
  InputAdornment,
  Button,
  Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';

function UpdateOrdersTab({ orders, loading, error, onOrderClick, getTotalNoteCount, handleOpenNotesDialog }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updateTypeFilter, setUpdateTypeFilter] = useState('all');
  
  // Filter orders by status, update type, and search query
  const filteredOrders = orders.filter(order => {
    // Filter by status
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    
    // Filter by update type
    if (updateTypeFilter !== 'all' && order.updateType !== updateTypeFilter) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const schoolName = (order.schoolName || '').toLowerCase();
      const orgName = (order.organizationName || '').toLowerCase();
      const businessName = (order.relatedOrderBusinessName || '').toLowerCase();
      const orderId = String(order.id || '');
      
      return schoolName.includes(searchLower) || 
             orgName.includes(searchLower) || 
             businessName.includes(searchLower) ||
             orderId.includes(searchLower);
    }
    
    return true;
  });

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'New': 'info',
      'In Progress': 'warning',
      'Completed': 'success',
      'Cancelled': 'error',
      'On Hold': 'default'
    };
    return statusColors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            placeholder="Search by school, organization, business name, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Update Type</InputLabel>
            <Select
              value={updateTypeFilter}
              onChange={(e) => setUpdateTypeFilter(e.target.value)}
              label="Update Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="Header Update">Header Update</MenuItem>
              <MenuItem value="Order Update">Order Update</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
              <MenuItem value="On Hold">On Hold</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            {searchQuery || statusFilter !== 'all' || updateTypeFilter !== 'all'
              ? 'No update orders match your filters'
              : 'No update orders found'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Update Type</strong></TableCell>
                <TableCell><strong>Organisation</strong></TableCell>
                <TableCell><strong>Details</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created By</strong></TableCell>
                <TableCell><strong>Date Created</strong></TableCell>
                <TableCell><strong>Notes</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => {
                const noteCount = getTotalNoteCount ? getTotalNoteCount('order', order.id) : 0;
                
                return (
                  <TableRow 
                    key={order.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onOrderClick && onOrderClick(order)}
                  >
                    <TableCell>#{order.id}</TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={order.updateType || 'Unknown'} 
                        size="small"
                        color={order.updateType === 'Header Update' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {order.organizationName || 'Unknown Organisation'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {order.updateType === 'Header Update' ? (
                        <Box>
                          {order.schoolName && (
                            <Typography variant="body2">
                              <strong>School:</strong> {order.schoolName}
                            </Typography>
                          )}
                          {order.headerProofUrl && (
                            <Typography variant="caption" color="textSecondary">
                              Has proof link
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          {order.relatedOrderBusinessName && (
                            <Typography variant="body2">
                              <strong>Business:</strong> {order.relatedOrderBusinessName}
                            </Typography>
                          )}
                          {order.relatedOrderId && (
                            <Typography variant="caption" color="textSecondary">
                              Order #{order.relatedOrderId}
                            </Typography>
                          )}
                          {order.orderUpdateType && (
                            <Chip 
                              label={order.orderUpdateType} 
                              size="small" 
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={order.status || 'New'} 
                        size="small"
                        color={getStatusColor(order.status)}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {order.createdByName || order.salesPersonName || 'Unknown'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateLong(order.createdAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      {handleOpenNotesDialog && (
                        <Tooltip title="View Notes">
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenNotesDialog('order', order.id, `Update Order #${order.id}`);
                            }}
                          >
                            <Badge badgeContent={noteCount} color="primary">
                              <NotesIcon />
                            </Badge>
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

UpdateOrdersTab.propTypes = {
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onOrderClick: PropTypes.func,
  getTotalNoteCount: PropTypes.func,
  handleOpenNotesDialog: PropTypes.func
};

UpdateOrdersTab.defaultProps = {
  orders: [],
  loading: false,
  error: null
};

export default UpdateOrdersTab;

